/**
 * CHỌN THÁNG — công tháng, công tuần, lịch & sự kiện.
 *
 * Ba màn con theo bản thiết kế `fe/bang-cham-cong-lich-thang.pdf`,
 * `fe/bang-cham-cong-tuan.pdf`, `fe/lich-va-su-kien.pdf`.
 *
 * Tất cả dựng từ MỘT lời gọi `GET /attendance/me?month=` — server trả mảng
 * ngày kèm `work_hours` và `segments`. Không có endpoint riêng cho tuần hay
 * cho lịch; cắt trong bộ nhớ là đúng vì một tháng chỉ có ~31 phần tử.
 *
 * ── Điều màn hình này CỐ Ý không hiển thị ──────────────────────────────────
 * Không có điểm tin cậy (docs/06 §6.4). Nhân viên không kiểm soát được con số
 * đó; hiện lên chỉ gây lo lắng, mà bản ghi chờ duyệt vẫn tự duyệt sau 24 giờ.
 */

import { useMemo, useState } from 'react'

import { useTaiDuLieu } from '../hooks/useTaiDuLieu'
import {
  dichThang, gio, gioTuSoThuc, ngayCong, nhanThang, soNgayTrongThang,
  thangHienTai, thuTrongTuan,
} from '../lib/gio'
import { layLichSu } from '../services/attendance.service'
import type { NgayCong } from '../types'
import { HopLoi, Icon, OTrong, XuongDong } from '../ui'

type ManCon = 'thang' | 'tuan' | 'su-kien'

const MAN_CON: Array<[ManCon, string]> = [
  ['thang', 'Công tháng'],
  ['tuan', 'Công tuần'],
  ['su-kien', 'Lịch & sự kiện'],
]

/** Ngày công "đủ" khi đạt mốc này. Server không trả ngưỡng nên client tự đặt. */
const GIO_DU_CONG = 8

export default function LichSu() {
  const [man, setMan] = useState<ManCon>('thang')
  const [thang, setThang] = useState(thangHienTai)

  const { du_lieu, dang_tai, loi, tai_lai } = useTaiDuLieu(
    () => layLichSu(thang), [thang],
  )

  const days = useMemo(() => du_lieu?.days ?? [], [du_lieu])

  /** Tra cứu nhanh theo ngày — dùng ở cả ba màn con. */
  const theoNgay = useMemo(() => {
    const m = new Map<string, NgayCong>()
    days.forEach((d) => m.set(d.date, d))
    return m
  }, [days])

  return (
    <div className="man-hinh" style={{ display: 'grid', gap: 'var(--k4)' }}>
      <nav className="tab-phu" aria-label="Chế độ xem">
        {MAN_CON.map(([gt, nhan]) => (
          <button
            key={gt}
            type="button"
            className={`tab-phu-muc ${man === gt ? 'dang-chon' : ''}`}
            aria-current={man === gt ? 'page' : undefined}
            onClick={() => setMan(gt)}
          >
            {nhan}
          </button>
        ))}
      </nav>

      <ChonThang
        thang={thang}
        doiThang={setThang}
        // Không cho đi tới tương lai: bảng công tháng sau luôn rỗng và người
        // dùng sẽ tưởng dữ liệu bị mất.
        toiDa={thangHienTai()}
      />

      {loi ? <HopLoi loi={loi} thuLai={tai_lai} /> : null}
      {dang_tai && !du_lieu && <XuongDong so={3} cao />}

      {du_lieu && (
        man === 'thang' ? <CongThang thang={thang} theoNgay={theoNgay} />
        : man === 'tuan' ? <CongTuan thang={thang} days={days} />
        : <LichSuKien thang={thang} theoNgay={theoNgay} />
      )}
    </div>
  )
}

/* ═══════════════════════════════════════════════════════════════════════════
 * CHỌN THÁNG
 * ═══════════════════════════════════════════════════════════════════════════ */

function ChonThang(
  { thang, doiThang, toiDa }: {
    thang: string
    doiThang: (t: string) => void
    toiDa: string
  },
) {
  const toiRoi = thang >= toiDa
  return (
    <div className="the-hom-nay-dau">
      <strong>{nhanThang(thang)}</strong>
      <div className="the-hom-nay-dieu-khien">
        <button type="button" className="nut-tron-nho" aria-label="Tháng trước"
                onClick={() => doiThang(dichThang(thang, -1))}>
          <Icon ten="trai" size={20} />
        </button>
        <button type="button" className="nut-tron-nho" aria-label="Tháng sau"
                disabled={toiRoi} onClick={() => doiThang(dichThang(thang, 1))}>
          <Icon ten="phai" size={20} />
        </button>
      </div>
    </div>
  )
}

/* ═══════════════════════════════════════════════════════════════════════════
 * CÔNG THÁNG — lưới lịch
 * ═══════════════════════════════════════════════════════════════════════════ */

function CongThang(
  { thang, theoNgay }: { thang: string; theoNgay: Map<string, NgayCong> },
) {
  const homNay = ngayCong()

  /* Lưới bắt đầu từ THỨ HAI. `getDay()` trả 0 cho Chủ nhật, nên phải xoay:
     CN (0) thành ô thứ 7, T2 (1) thành ô thứ 1. */
  const o = useMemo(() => {
    const soNgay = soNgayTrongThang(thang)
    const dauThang = `${thang}-01`
    const lech = (thuTrongTuan(dauThang) + 6) % 7

    const ds: Array<{ ngay: string | null; so: number }> = []
    for (let i = 0; i < lech; i++) ds.push({ ngay: null, so: 0 })
    for (let d = 1; d <= soNgay; d++) {
      ds.push({ ngay: `${thang}-${String(d).padStart(2, '0')}`, so: d })
    }
    // Lấp nốt hàng cuối để lưới không bị khuyết góc.
    while (ds.length % 7 !== 0) ds.push({ ngay: null, so: 0 })
    return ds
  }, [thang])

  const tong = useMemo(() => {
    let gioLam = 0
    let duCong = 0
    let coDiLam = 0
    theoNgay.forEach((d) => {
      if (!d.date.startsWith(thang)) return
      gioLam += d.work_hours ?? 0
      if ((d.work_hours ?? 0) > 0) coDiLam++
      if ((d.work_hours ?? 0) >= GIO_DU_CONG) duCong++
    })
    return { gioLam, duCong, coDiLam }
  }, [theoNgay, thang])

  return (
    <>
      <div className="o-thong-ke">
        <div className="o-thong-ke-muc">
          <span className="o-thong-ke-nhan">Tổng giờ làm</span>
          <span className="o-thong-ke-so">{gioTuSoThuc(tong.gioLam)}</span>
        </div>
        <div className="o-thong-ke-muc tot">
          <span className="o-thong-ke-nhan">Đủ công</span>
          <span className="o-thong-ke-so">{tong.duCong} ngày</span>
        </div>
        <div className="o-thong-ke-muc">
          <span className="o-thong-ke-nhan">Có đi làm</span>
          <span className="o-thong-ke-so">{tong.coDiLam} ngày</span>
        </div>
      </div>

      <div className="lich-thang">
        <div className="lich-dau-tuan" aria-hidden="true">
          {['T2', 'T3', 'T4', 'T5', 'T6', 'T7', 'CN'].map((t) => <span key={t}>{t}</span>)}
        </div>
        <div className="lich-luoi">
          {o.map((x, i) => {
            if (!x.ngay) return <div key={`trong-${i}`} className="lich-o ngoai-thang" />

            const d = theoNgay.get(x.ngay)
            const soGio = d?.work_hours ?? 0
            const laCN = thuTrongTuan(x.ngay) === 0
            const laHomNay = x.ngay === homNay

            const lop = laHomNay ? 'hom-nay'
              : soGio >= GIO_DU_CONG ? 'du-cong'
              : soGio > 0 ? 'thieu-cong'
              : laCN ? 'nghi' : ''

            return (
              <div
                key={x.ngay}
                className={`lich-o ${lop}`}
                title={soGio > 0
                  ? `${x.so}/${thang.slice(5)}: ${gioTuSoThuc(soGio)}`
                  : `${x.so}/${thang.slice(5)}: không có bản ghi`}
              >
                <span className="lich-ngay">{x.so}</span>
                <span className="lich-gia-tri">
                  {soGio > 0 ? soGio.toFixed(soGio % 1 === 0 ? 0 : 1) : laCN ? 'N' : '0'}
                </span>
              </div>
            )
          })}
        </div>
      </div>

      <div className="chu-giai-lich">
        <strong style={{ fontSize: 'var(--cy-muc)' }}>Ghi chú</strong>
        <span className="chu-giai-dong">
          <i className="chu-giai-o" style={{ background: 'var(--chinh-nhat)' }} />
          Xanh lá: làm đủ {GIO_DU_CONG} giờ trở lên
        </span>
        <span className="chu-giai-dong">
          <i className="chu-giai-o" style={{ background: 'var(--canh-bao-nen)' }} />
          Cam: ngày hôm nay
        </span>
        <span className="chu-giai-dong">
          <i className="chu-giai-o" style={{ background: '#fff' }} />
          Trắng có số: có đi làm nhưng chưa đủ {GIO_DU_CONG} giờ
        </span>
        <span className="chu-giai-dong">
          <i className="chu-giai-o" style={{ background: '#fff' }} />
          <strong>N</strong>: Chủ nhật
        </span>
        <p className="ghi-chu-rieng-tu" style={{ textAlign: 'left', maxWidth: 'none' }}>
          Số trong ô là <strong>số giờ công</strong> hệ thống ghi nhận. Ngày
          chờ quản lý duyệt vẫn hiện số — bản ghi chờ duyệt tự động được duyệt
          sau 24 giờ.
        </p>
      </div>
    </>
  )
}

/* ═══════════════════════════════════════════════════════════════════════════
 * CÔNG TUẦN
 * ═══════════════════════════════════════════════════════════════════════════ */

function CongTuan({ thang, days }: { thang: string; days: NgayCong[] }) {
  /* Cắt tháng thành các tuần Thứ Hai → Chủ nhật. Tuần đầu và tuần cuối có thể
     thiếu ngày; đó là đúng — chúng bị cắt bởi ranh giới tháng. */
  const tuan = useMemo(() => {
    const soNgay = soNgayTrongThang(thang)
    const nhom: string[][] = []
    let hienTai: string[] = []

    for (let d = 1; d <= soNgay; d++) {
      const iso = `${thang}-${String(d).padStart(2, '0')}`
      hienTai.push(iso)
      if (thuTrongTuan(iso) === 0) { nhom.push(hienTai); hienTai = [] }
    }
    if (hienTai.length > 0) nhom.push(hienTai)
    return nhom
  }, [thang])

  const homNay = ngayCong()
  const tuanChuaHomNay = tuan.findIndex((t) => t.includes(homNay))
  const [chon, setChon] = useState(() => (tuanChuaHomNay >= 0 ? tuanChuaHomNay : 0))

  const theoNgay = useMemo(() => {
    const m = new Map<string, NgayCong>()
    days.forEach((d) => m.set(d.date, d))
    return m
  }, [days])

  const dsNgay = tuan[Math.min(chon, tuan.length - 1)] ?? []

  const tong = dsNgay.reduce((s, iso) => s + (theoNgay.get(iso)?.work_hours ?? 0), 0)
  const duCong = dsNgay.filter(
    (iso) => (theoNgay.get(iso)?.work_hours ?? 0) >= GIO_DU_CONG).length
  const thieuCong = dsNgay.filter((iso) => {
    const h = theoNgay.get(iso)?.work_hours ?? 0
    return h > 0 && h < GIO_DU_CONG
  }).length

  return (
    <>
      <div className="hang-chon-tuan">
        <label>
          <span className="chi-doc-man-hinh">Chọn tuần</span>
          <select value={chon} onChange={(e) => setChon(Number(e.target.value))}>
            {tuan.map((t, i) => (
              <option key={t[0]} value={i}>
                Tuần {i + 1} ({Number(t[0].slice(8))}–{Number(t.at(-1)!.slice(8))})
              </option>
            ))}
          </select>
        </label>
      </div>

      <div className="o-thong-ke">
        <div className="o-thong-ke-muc">
          <span className="o-thong-ke-nhan">Tổng giờ làm</span>
          <span className="o-thong-ke-so">{gioTuSoThuc(tong)}</span>
        </div>
        <div className="o-thong-ke-muc tot">
          <span className="o-thong-ke-nhan">Đủ công</span>
          <span className="o-thong-ke-so">{duCong}/{dsNgay.length} ngày</span>
        </div>
        <div className="o-thong-ke-muc loi">
          <span className="o-thong-ke-nhan">Thiếu công</span>
          <span className="o-thong-ke-so">{thieuCong} ngày</span>
        </div>
      </div>

      <div className="the-khoi" style={{ padding: 0, overflow: 'hidden' }}>
        <table className="bang-tuan">
          <thead>
            <tr>
              <th>Thứ</th>
              <th>Ngày</th>
              <th>Vào</th>
              <th>Ra</th>
              <th>Tổng giờ</th>
              <th aria-label="Trạng thái" />
            </tr>
          </thead>
          <tbody>
            {dsNgay.map((iso) => {
              const d = theoNgay.get(iso)
              const soGio = d?.work_hours ?? 0
              // Giờ vào/ra lấy từ chặng làm việc đầu và cuối trong ngày. Server
              // không trả riêng hai mốc này ở endpoint của nhân viên.
              const chang = d?.segments?.filter((s) => s.kind === 'work') ?? []
              const vao = chang[0]?.start
              const ra = chang.at(-1)?.end

              const mau = soGio >= GIO_DU_CONG ? 'var(--chinh)'
                : soGio > 0 ? 'var(--lam)'
                : 'var(--vien-dam)'

              return (
                <tr key={iso} className={iso === homNay ? 'hom-nay' : ''}>
                  <td>{['CN', 'T2', 'T3', 'T4', 'T5', 'T6', 'T7'][thuTrongTuan(iso)]}</td>
                  <td>{iso.slice(8)}/{iso.slice(5, 7)}</td>
                  <td>{vao ? gio(vao) : '–:–'}</td>
                  <td>{ra ? gio(ra) : '–:–'}</td>
                  <td className="cot-gio">{soGio > 0 ? gioTuSoThuc(soGio) : '–'}</td>
                  <td>
                    <i className="cham-tron" style={{ background: mau }}
                       aria-label={soGio >= GIO_DU_CONG ? 'Đủ công'
                         : soGio > 0 ? 'Thiếu giờ' : 'Không có bản ghi'} />
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>

      <div className="chu-giai-lich">
        <strong style={{ fontSize: 'var(--cy-muc)' }}>Ghi chú</strong>
        <span className="chu-giai-dong">
          <i className="cham-tron" style={{ background: 'var(--chinh)' }} />
          Xanh lá: làm đủ {GIO_DU_CONG} giờ trở lên trong ngày
        </span>
        <span className="chu-giai-dong">
          <i className="cham-tron" style={{ background: 'var(--lam)' }} />
          Xanh dương: có đi làm nhưng chưa đủ {GIO_DU_CONG} giờ
        </span>
        <span className="chu-giai-dong">
          <i className="cham-tron" style={{ background: 'var(--vien-dam)' }} />
          Xám: không có bản ghi nào
        </span>
      </div>
    </>
  )
}

/* ═══════════════════════════════════════════════════════════════════════════
 * LỊCH & SỰ KIỆN
 * ═══════════════════════════════════════════════════════════════════════════ */

/**
 * Bản thiết kế `fe/lich-va-su-kien.pdf` vẽ hai loại sự kiện: NGÀY NGHỈ LỄ và
 * SINH NHẬT đồng nghiệp. Hệ thống hiện KHÔNG có dữ liệu cho cả hai:
 *
 *   · Không bảng `holidays`, không endpoint ngày lễ.
 *   · Bảng `employees` không có cột ngày sinh (xem `db/init/001`).
 *
 * Vì vậy màn này hiển thị thứ nó THẬT SỰ biết — dải lịch tuần kèm chấm đánh
 * dấu ngày có chấm công — và nói thẳng phần còn lại chưa có. Bịa vài ngày lễ
 * vào đây là tệ hơn để trống: nhân viên sẽ nghỉ theo một cái lịch không ai
 * duyệt.
 */
function LichSuKien(
  { thang, theoNgay }: { thang: string; theoNgay: Map<string, NgayCong> },
) {
  const homNay = ngayCong()
  const trongThangNay = homNay.startsWith(thang)

  /* Dải bảy ngày: tuần chứa hôm nay nếu đang xem tháng này, không thì tuần
     đầu tiên của tháng được chọn. */
  const dai = useMemo(() => {
    const goc = trongThangNay ? homNay : `${thang}-01`
    const lech = (thuTrongTuan(goc) + 6) % 7
    const d0 = new Date(`${goc}T00:00:00`)
    d0.setDate(d0.getDate() - lech)
    return Array.from({ length: 7 }, (_, i) => {
      const d = new Date(d0)
      d.setDate(d0.getDate() + i)
      return ngayCong(d)
    })
  }, [thang, homNay, trongThangNay])

  const coCong = dai.filter((iso) => (theoNgay.get(iso)?.work_hours ?? 0) > 0)

  return (
    <>
      <div className="dai-lich">
        <div className="dai-lich-dau">
          <strong>{nhanThang(thang)}</strong>
        </div>
        <div className="dai-lich-tuan">
          {dai.map((iso) => {
            const co = (theoNgay.get(iso)?.work_hours ?? 0) > 0
            return (
              <div key={iso} className={`dai-lich-o ${iso === homNay ? 'hom-nay' : ''}`}>
                <span className="dai-thu">
                  {['CN', 'T2', 'T3', 'T4', 'T5', 'T6', 'T7'][thuTrongTuan(iso)]}
                </span>
                <span className="dai-ngay">{Number(iso.slice(8))}</span>
                <i className="dai-cham"
                   style={{ background: co ? 'var(--canh-bao)' : 'transparent' }}
                   aria-label={co ? 'Có chấm công' : undefined} />
              </div>
            )
          })}
        </div>
        <p className="ghi-chu-rieng-tu" style={{ textAlign: 'left', maxWidth: 'none' }}>
          Chấm cam: ngày có bản ghi chấm công. Tuần này bạn có{' '}
          <strong>{coCong.length}</strong> ngày.
        </p>
      </div>

      <section style={{ display: 'grid', gap: 'var(--k3)' }}>
        <h2 style={{ fontSize: 'var(--cy-trang)' }}>Sự kiện sắp tới</h2>

        {/* Nói thẳng vì sao trống, thay vì một danh sách rỗng không giải thích. */}
        <div className="the-khoi">
          <OTrong
            icon="lich"
            tieu_de="Chưa có lịch sự kiện"
            mo_ta="Hệ thống chưa quản lý ngày nghỉ lễ và sinh nhật nhân viên. Khi
                   công ty khai báo lịch nghỉ, mục này sẽ hiện ra ở đây."
          />
        </div>

        <div className="the-khoi">
          <p style={{ margin: 0, fontSize: 'var(--cy-nho)', color: 'var(--chu-phu)' }}>
            Nghỉ lễ và ngày nghỉ bù hiện được thông báo qua kênh nội bộ của công
            ty, không qua ứng dụng này. Nếu bạn nghỉ vào ngày làm việc, hãy gửi
            đơn ở tab <strong>Yêu cầu</strong> để bảng công không thiếu ngày.
          </p>
        </div>
      </section>
    </>
  )
}
